import type { CaseEntryType } from '../types/caseEntry'
import type { QuoteLine } from '../types/quote'
import { formatKr, lineSubtotal } from './quote'

/**
 * Tilbuddet er estimatet/aftalen; sagen er det faktiske forbrug.
 * Tilbudslinjer har ingen type, så typen gættes ud fra enhed og beskrivelse.
 */
export function guessEntryType(line: QuoteLine): CaseEntryType {
  const text = (line.description || '').toLowerCase()
  const unit = line.unit as string
  if (unit === 'timer') return 'Timer'
  if (unit === 'km' || /kørsel|kilometer|transport/.test(text)) return 'Kørsel'
  if (/servicevogn|værktøj/.test(text)) return 'Servicevogn og værktøj'
  if (/maskin|leje|vibrator|minigraver|gravemaskine|brænder|fræser|kompressor/.test(text)) return 'Maskiner'
  if (/bortskaf|affald|genbrugs|container|deponi|tipgebyr/.test(text)) return 'Bortskaffelse'
  if (unit === 'fast pris') return 'Andet'
  return 'Materialer'
}

export type EstimateKind = 'Timer' | 'Materialer' | 'Øvrigt'

export function estimateKind(line: QuoteLine): EstimateKind {
  const type = guessEntryType(line)
  if (type === 'Timer' || type === 'Materialer') return type
  return 'Øvrigt'
}

/** Sagstyper der samles under "Øvrigt". */
export const otherEntryTypes: CaseEntryType[] = ['Kørsel', 'Servicevogn og værktøj', 'Maskiner', 'Bortskaffelse', 'Andet']

export interface EstimateTotals {
  hours: number
  hoursCount: number
  materials: number
  other: number
  subtotal: number
  vatAmount: number
  total: number
}

export function estimateTotals(lines: QuoteLine[]): EstimateTotals {
  const sumOf = (kind: EstimateKind) =>
    lines.filter((line) => estimateKind(line) === kind).reduce((sum, line) => sum + lineSubtotal(line), 0)
  const subtotal = lines.reduce((sum, line) => sum + lineSubtotal(line), 0)
  const vatAmount = lines.reduce((sum, line) => sum + lineSubtotal(line) * ((line.vatRate || 0) / 100), 0)
  return {
    hours: sumOf('Timer'),
    hoursCount: lines
      .filter((line) => estimateKind(line) === 'Timer')
      .reduce((sum, line) => sum + (line.quantity || 0), 0),
    materials: sumOf('Materialer'),
    other: sumOf('Øvrigt'),
    subtotal,
    vatAmount,
    total: subtotal + vatAmount,
  }
}

const round2 = (value: number) => Math.round(value * 100) / 100

export function formatQuantity(value: number): string {
  return value.toLocaleString('da-DK', { maximumFractionDigits: 2 })
}

export function formatHours(value: number): string {
  const text = value.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 2 })
  return `${text} ${value === 1 ? 'time' : 'timer'}`
}

/** "+70 kr." / "−155 kr." / "0 kr." */
export function signedKr(value: number): string {
  const rounded = round2(value)
  if (rounded === 0) return formatKr(0)
  return `${rounded > 0 ? '+' : '−'}${formatKr(Math.abs(rounded))}`
}

/** "+1,0 timer" / "−0,5 timer" */
export function signedHours(value: number): string {
  const rounded = round2(value)
  if (rounded === 0) return formatHours(0)
  return `${rounded > 0 ? '+' : '−'}${formatHours(Math.abs(rounded))}`
}

/** Over estimatet markeres diskret; under eller lig vises neutralt. */
export function diffTone(value: number): string {
  return round2(value) > 0 ? 'text-danger' : 'text-ink-soft'
}
